import api from './api.service';

type ApiBody<T> = { success?: boolean; data?: T; message?: string };

async function unwrap<T>(promise: Promise<unknown>): Promise<T> {
  const body = (await promise) as ApiBody<T>;
  if (body?.data === undefined) throw new Error(body?.message || 'Estimating library request failed');
  return body.data;
}

/** Company-scoped EST3 estimating knowledge libraries — full CRUD. */
export const epcEstimatingLibraryService = {
  listLabourClasses: () => unwrap<any[]>(api.get('/epc/wbs-libraries/resource-classes/labour')),
  createLabourClass: (body: Record<string, unknown>) =>
    unwrap<unknown>(api.post('/epc/wbs-libraries/resource-classes/labour', body)),
  updateLabourClass: (classId: string, body: Record<string, unknown>) =>
    unwrap<unknown>(api.patch(`/epc/wbs-libraries/resource-classes/labour/${classId}`, body)),
  deleteLabourClass: (classId: string) =>
    unwrap<unknown>(api.delete(`/epc/wbs-libraries/resource-classes/labour/${classId}`)),

  listEquipmentClasses: () =>
    unwrap<any[]>(api.get('/epc/wbs-libraries/resource-classes/equipment')),
  createEquipmentClass: (body: Record<string, unknown>) =>
    unwrap<unknown>(api.post('/epc/wbs-libraries/resource-classes/equipment', body)),
  updateEquipmentClass: (classId: string, body: Record<string, unknown>) =>
    unwrap<unknown>(api.patch(`/epc/wbs-libraries/resource-classes/equipment/${classId}`, body)),
  deleteEquipmentClass: (classId: string) =>
    unwrap<unknown>(api.delete(`/epc/wbs-libraries/resource-classes/equipment/${classId}`)),

  listLabourRates: (resourceClassId?: string) =>
    unwrap<any[]>(
      api.get('/epc/wbs-libraries/labour-rates', {
        params: resourceClassId ? { resourceClassId } : undefined,
      })
    ),
  createLabourRate: (body: Record<string, unknown>) =>
    unwrap<unknown>(api.post('/epc/wbs-libraries/labour-rates', body)),
  updateLabourRate: (rateId: string, body: Record<string, unknown>) =>
    unwrap<unknown>(api.patch(`/epc/wbs-libraries/labour-rates/${rateId}`, body)),
  deleteLabourRate: (rateId: string) =>
    unwrap<unknown>(api.delete(`/epc/wbs-libraries/labour-rates/${rateId}`)),

  listEquipmentRates: (equipmentClassId?: string) =>
    unwrap<any[]>(
      api.get('/epc/wbs-libraries/equipment-rates', {
        params: equipmentClassId ? { equipmentClassId } : undefined,
      })
    ),
  createEquipmentRate: (body: Record<string, unknown>) =>
    unwrap<unknown>(api.post('/epc/wbs-libraries/equipment-rates', body)),
  updateEquipmentRate: (rateId: string, body: Record<string, unknown>) =>
    unwrap<unknown>(api.patch(`/epc/wbs-libraries/equipment-rates/${rateId}`, body)),
  deleteEquipmentRate: (rateId: string) =>
    unwrap<unknown>(api.delete(`/epc/wbs-libraries/equipment-rates/${rateId}`)),

  listCrewTemplates: () => unwrap<any[]>(api.get('/epc/wbs-libraries/crew-templates')),
  createCrewTemplate: (body: Record<string, unknown>) =>
    unwrap<unknown>(api.post('/epc/wbs-libraries/crew-templates', body)),
  updateCrewTemplate: (templateId: string, body: Record<string, unknown>) =>
    unwrap<unknown>(api.patch(`/epc/wbs-libraries/crew-templates/${templateId}`, body)),
  deleteCrewTemplate: (templateId: string) =>
    unwrap<unknown>(api.delete(`/epc/wbs-libraries/crew-templates/${templateId}`)),
  listCrewMembers: (templateId: string) =>
    unwrap<any[]>(api.get(`/epc/wbs-libraries/crew-templates/${templateId}/members`)),
  addCrewMember: (templateId: string, body: Record<string, unknown>) =>
    unwrap<unknown>(api.post(`/epc/wbs-libraries/crew-templates/${templateId}/members`, body)),
  updateCrewMember: (templateId: string, memberId: string, body: Record<string, unknown>) =>
    unwrap<unknown>(
      api.patch(`/epc/wbs-libraries/crew-templates/${templateId}/members/${memberId}`, body)
    ),
  deleteCrewMember: (templateId: string, memberId: string) =>
    unwrap<unknown>(
      api.delete(`/epc/wbs-libraries/crew-templates/${templateId}/members/${memberId}`)
    ),

  listProductivityNorms: () => unwrap<any[]>(api.get('/epc/wbs-libraries/productivity-norms')),
  createProductivityNorm: (body: Record<string, unknown>) =>
    unwrap<unknown>(api.post('/epc/wbs-libraries/productivity-norms', body)),
  updateProductivityNorm: (normId: string, body: Record<string, unknown>) =>
    unwrap<unknown>(api.patch(`/epc/wbs-libraries/productivity-norms/${normId}`, body)),
  deleteProductivityNorm: (normId: string) =>
    unwrap<unknown>(api.delete(`/epc/wbs-libraries/productivity-norms/${normId}`)),

  listAssemblies: () => unwrap<any[]>(api.get('/epc/wbs-libraries/assemblies')),
  getAssembly: (assemblyId: string) =>
    unwrap<any>(api.get(`/epc/wbs-libraries/assemblies/${assemblyId}`)),
  createAssembly: (body: Record<string, unknown>) =>
    unwrap<unknown>(api.post('/epc/wbs-libraries/assemblies', body)),
  updateAssembly: (assemblyId: string, body: Record<string, unknown>) =>
    unwrap<unknown>(api.patch(`/epc/wbs-libraries/assemblies/${assemblyId}`, body)),
  deleteAssembly: (assemblyId: string) =>
    unwrap<unknown>(api.delete(`/epc/wbs-libraries/assemblies/${assemblyId}`)),
  createAssemblyVersion: (assemblyId: string) =>
    unwrap<unknown>(api.post(`/epc/wbs-libraries/assemblies/${assemblyId}/versions`, {})),
  publishAssemblyVersion: (versionId: string) =>
    unwrap<unknown>(api.post(`/epc/wbs-libraries/assembly-versions/${versionId}/publish`, {})),
  listAssemblyComponents: (versionId: string) =>
    unwrap<any[]>(api.get(`/epc/wbs-libraries/assembly-versions/${versionId}/components`)),
  createAssemblyComponent: (versionId: string, body: Record<string, unknown>) =>
    unwrap<unknown>(api.post(`/epc/wbs-libraries/assembly-versions/${versionId}/components`, body)),
  updateAssemblyComponent: (
    versionId: string,
    componentId: string,
    body: Record<string, unknown>
  ) =>
    unwrap<unknown>(
      api.patch(`/epc/wbs-libraries/assembly-versions/${versionId}/components/${componentId}`, body)
    ),
  deleteAssemblyComponent: (versionId: string, componentId: string) =>
    unwrap<unknown>(
      api.delete(`/epc/wbs-libraries/assembly-versions/${versionId}/components/${componentId}`)
    ),
};
